import { Price } from '../models/product/price'
import { PriceDetails } from '../models/product/price-details'
import { ABSOLUTE_QUANT, RELATIVE_QUANT, ThemeConfig } from './theme-config';
import chaining from './chaining';

const quantityType = ThemeConfig.get<string>('catalog', 'quantity-type')

/* The direct implementation of these interface is not necessary in TS. */

export interface CalculableProduct {
    price: PriceDetails
}

export interface CalculableLine {
    discount: number
    quantity: number
    product: CalculableProduct
}

/**
 * Calculates the absolute quantity for a product from relative quantity.
 * @param product The product to calculate for.
 * @param relativeQuantity The relative quantity.
 */
export function absoluteQuantity(product: CalculableProduct, relativeQuantity: number): number {
    return product.price.stepping * relativeQuantity
}

/**
 * Calculates the relative quantity for a product from the absolute quantity.
 * @param product The product to calculate for.
 * @param absoluteQuantity The absolute quantity.
 */
export function relativeQuantity(product: CalculableProduct, absoluteQuantity: number): number {
    return absoluteQuantity / product.price.stepping
}

/**
 * Calculates the current unitprice for the product based on absolute quantity.
 * @param product The product to calculate the unit prict for.
 * @param absoluteQuantity The absolute quantity to use in calculation.
 */
export function unitPrice(product: CalculableProduct, absoluteQuantity: number): Price {
    return product && product.price && product.price.prices && product.price.prices.find((current, index, prices) => {
        let next = prices[index + 1]

        return !next 
            ? absoluteQuantity >= current.minQuantity
            : absoluteQuantity >= current.minQuantity && absoluteQuantity < next.minQuantity
    }) || {
        currency: 'EUR',
        minQuantity: 1,
        price: 0
    }
}

/**
 * Determines the customer currency from a product.
 * @param products Product to use for currency determination.
 */
export function currency(products: CalculableProduct[]): string {
    return (products.length && products[0].price.prices[0].currency) || 'EUR'
}

/**
 * Calculates the minimum relative order quantity for a product.
 * @param product Product for which minimum quantity should be calculated.
 */
export function minRelativeQuantity(product: CalculableProduct): number {
    return product.price.minQuantity / product.price.stepping
}

/**
 * Calculates the config based minimum order quantity.
 * @param product Product for which to calculate minimum quantity.
 */
export function minQuantity(product: CalculableProduct): number {
    return quantityType === RELATIVE_QUANT
        ? minRelativeQuantity(product)
        : chaining(product, 'price.minQuantity')
}

/**
 * Calculates the config based stepping.
 * @param product Product to calculate the stepping for.
 */
export function stepping(product: CalculableProduct): number {
    return quantityType == ABSOLUTE_QUANT
        ? product.price.stepping : 1
}

/**
 * Calculates the upwards config based quantity from the quantity factor.
 * @param product The product used to calculate the quantity for.
 * @param quantity The quantity factor.
 */
export function upwardsQuantity(product: CalculableProduct, quantity: number): number {
    return quantityType === RELATIVE_QUANT
        ? absoluteQuantity(product, quantity)
        : quantity
}

/**
 * Calculates the downwards config based quantity from the quantity factor.
 * @param product The product used to calculate the quantity for.
 * @param quantity The quantity factor.
 */
export function downwardsQuantity(product: CalculableProduct, quantity: number): number {
    return quantityType === RELATIVE_QUANT
        ? relativeQuantity(product, quantity)
        : quantity
}

/**
 * Calculates a usable percentage number for calculations,
 * e.g. 5 % will become 0.95.
 * @param percentage The percentage points
 */
export function discountPercentage(percentage: number): number {
    return (100 - percentage) / 100
}

/**
 * Calculates a usable reversed percentage number for calculations,
 * e.g. 5 % will become 1.05 
 * @param percentage The percentage points.
 */
export function reverseDiscountPercentage(percentage: number): number {
    return (100 + percentage) / 100
}

/**
 * Calculates the price w/o the previously applied discount.
 * @param price The price including discount.
 * @param discount The discount percentage.
 */
export function reverseDiscount(price: number, discount: number): number {
    return price * reverseDiscountPercentage(discount)
}
